import { Component, computed, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { ILoginState } from 'src/app/login/store/state';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';
import { getRelaxedUiAuth } from '../authorizations/selector';
import { FeatureComponent } from 'src/app/utils/feature-component';
import { LeftNavigation } from 'src/app/features/left-nav';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { getLoggedUserRole } from 'src/app/store/createManageUser/selector';
import { ICreateManageUserState, IUserList } from 'src/app/store/createManageUser/state';

@Component({
  selector: 'app-left-nav',
  template: `
    <app-left-nav-view
      [navFeatures]="navFeatures()"
      [eligActive]="eligActive()"
      [userAdminActive]="userAdminActive()"
      [profileActive]="profileActive()"></app-left-nav-view>
  `,
  standalone: false
})
export class LeftNavContainer extends FeatureComponent<typeof LeftNavigation> implements OnDestroy {
  readonly luxId = 'BMT-LeftNavContainer' + new Date().toISOString();
  readonly userAdminRoutes = ['/userAdmin/create', '/userAdmin/manage', '/manage-user'];
  readonly eligRoutes = ['/eligibility/add-member', '/eligibility/member', '/eligibility/family-enrollment'];
  readonly profileRoutes = ['/profile/authorizations', '/profile/updatePassword', '/profile/mfa'];
  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ),
    { initialValue: null as NavigationEnd | null }
  );
  readonly relaxedAuth = toSignal(this.lux.get(getRelaxedUiAuth, this.luxId), {
    initialValue: {} as Partial<IUiAuthorizations>
  });
  readonly loggedUser = toSignal(this.lux.get(getLoggedUserRole, this.luxId), {
    initialValue: [] as IUserList[]
  });
  readonly navFeatures = computed(() => {
    const relaxedAuth = this.relaxedAuth();
    const leftNavFeatures = 'leftnav' in relaxedAuth ? (relaxedAuth.leftnav || []) : [];
    const baseNavFeatures = leftNavFeatures.length ? ['profile', ...leftNavFeatures] : [];
    const loggedUser = this.loggedUser();
    const hasProducerToolkitAccess = Array.isArray(loggedUser)
      && !!loggedUser[0]?.hasProducerToolkitAuth;

    return hasProducerToolkitAccess
      ? ['hasProducerToolkitAccess', ...baseNavFeatures]
      : baseNavFeatures;
  });
  readonly activeUrl = computed(() => this.currentUrl()?.urlAfterRedirects || this.currentUrl()?.url || this.router.url);
  readonly eligActive = computed(() => this.eligRoutes.includes(this.activeUrl()));
  readonly userAdminActive = computed(() => this.userAdminRoutes.includes(this.activeUrl()));
  readonly profileActive = computed(() => this.profileRoutes.includes(this.activeUrl()));

  constructor(
    private readonly router: Router,
    private readonly lux: Lux<{ login: ILoginState, appUiAuth: IAppAuthorizations, createManageUserState: ICreateManageUserState }>
  ) {
    super([]);
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }
}
